import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../landing/Header';
import Footer from '../landing/Footer';

// Privacy policy of the website, the listings (/<country>/immobilier) and the Recouvro Immobilier
// app. Linked from the footer and from the app's "À propos" screen (Google Play requires it).
// Keep it true to the code: listings/stats.py, listings/alerts.py, mobile/src/lib/location.ts.
const UPDATED = '26 octobre 2026';
const CONTACT = 'jagovi@staff.recouvro.com';

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-xl font-semibold text-gray-900 mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function PrivacyPage() {
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Helmet>
        <title>Politique de confidentialité - Recouvro</title>
        <meta name="description" content="Les données que Recouvro et l'application Recouvro Immobilier utilisent, pourquoi, combien de temps, et vos droits." />
      </Helmet>
      <Header />
      <main className="flex-grow py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-sm p-6 sm:p-10">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Politique de confidentialité</h1>
            <p className="text-sm text-gray-500 mb-8">Dernière mise à jour : {UPDATED}</p>

            <div className="space-y-8 text-gray-600 leading-relaxed">
              <Section title="Qui sommes-nous ?">
                <p>
                  Recouvro est édité par DIGHITEC INNOVATIONS, SARL domiciliée à Abomey-Calavi (Bénin), IFU 3202322510966.
                  Cette politique s'applique au site recouvro.com, aux annonces immobilières (recouvro.com/bj/immobilier)
                  et à l'application mobile Recouvro Immobilier. Pour toute question : <a className="text-blue-600 underline" href={`mailto:${CONTACT}`}>{CONTACT}</a>.
                </p>
                <p>
                  Les annonces et l'application s'utilisent <strong>sans compte</strong> : nous ne vous demandons ni nom,
                  ni mot de passe, ni numéro de téléphone pour les consulter.
                </p>
              </Section>

              <Section title="Les données que nous utilisons, et pourquoi">
                <p><strong>Statistiques de visite.</strong> Pour savoir combien de personnes consultent les annonces et lesquelles intéressent,
                  nous comptons les actions (ouverture d'une annonce, lecture d'une vidéo, clic sur WhatsApp ou Appeler, partage).
                  Chaque navigateur ou téléphone reçoit un identifiant aléatoire (un cookie sur le site) ; nous n'en conservons
                  qu'une empreinte chiffrée, qui ne permet pas de vous identifier. Nous n'y associons ni votre nom, ni votre adresse IP.
                  Sur le site, Google Analytics mesure aussi l'audience (pages vues, type d'appareil, pays).</p>
                <p><strong>Votre position (application, facultatif).</strong> Si vous l'autorisez, l'application lit la position du téléphone
                  pour afficher les annonces de votre ville. Cette position est utilisée <strong>uniquement sur votre téléphone</strong> pour trouver
                  le nom de la ville : elle n'est jamais envoyée à nos serveurs ni conservée. Vous pouvez retirer l'autorisation à tout moment
                  dans les réglages du téléphone.</p>
                <p><strong>Alertes (facultatif).</strong> Si vous créez une alerte, nous conservons les critères choisis (type de bien, ville, budget…)
                  et, selon votre choix, votre adresse e-mail ou l'identifiant de notification de votre téléphone. Ils servent uniquement à vous envoyer
                  les nouvelles annonces correspondantes. Une alerte par e-mail ne démarre qu'après confirmation par le lien reçu.
                  Chaque e-mail contient un bouton pour la désactiver ; dans l'application, elle se désactive depuis l'écran Alerte.</p>
                <p><strong>Contact d'un agent.</strong> Les boutons WhatsApp et Appeler ouvrent WhatsApp ou le téléphone pour contacter
                  directement l'agent qui a publié l'annonce. Nous ne voyons pas vos échanges. Le message WhatsApp proposé contient le lien de l'annonce.</p>
                <p><strong>Journaux techniques.</strong> Comme tout site, nos serveurs enregistrent les requêtes reçues (adresse IP, navigateur, page demandée)
                  pour la sécurité et le bon fonctionnement du service.</p>
              </Section>

              <Section title="Les annonces">
                <p>
                  Les annonces sont des publications publiques d'agents immobiliers sur TikTok et Facebook. Nous les affichons avec un lien
                  vers la publication d'origine et le profil de l'agent ; les vidéos sont lues avec le lecteur de TikTok ou de Facebook.
                  Le prix, le quartier et le type de bien sont lus automatiquement dans le texte de l'annonce (par un outil d'intelligence artificielle) :
                  vérifiez-les auprès de l'agent. Un agent qui souhaite retirer ses annonces peut nous écrire à {CONTACT}.
                </p>
              </Section>

              <Section title="Avec qui ces données sont partagées">
                <p>Nous ne vendons aucune donnée. Elles sont traitées, pour notre compte uniquement, par :</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Hetzner Online (Allemagne) : hébergement des serveurs et des images ;</li>
                  <li>Zoho ZeptoMail : envoi des e-mails d'alerte ;</li>
                  <li>Expo : envoi des notifications de l'application ;</li>
                  <li>Google Analytics : mesure d'audience du site.</li>
                </ul>
              </Section>

              <Section title="Combien de temps">
                <ul className="list-disc pl-6 space-y-1">
                  <li>Statistiques par visiteur : 35 jours, puis seuls les totaux par jour sont gardés ;</li>
                  <li>Alertes : tant qu'elles existent ; une alerte désactivée est supprimée sur simple demande ;</li>
                  <li>Position : jamais conservée.</li>
                </ul>
              </Section>

              <Section title="Vos droits">
                <p>
                  Conformément au Code du numérique du Bénin (loi n° 2017-20), vous pouvez demander l'accès à vos données, leur correction
                  ou leur suppression (par exemple la suppression d'une alerte et de votre adresse e-mail) en écrivant à {CONTACT}.
                  Vous pouvez aussi saisir l'Autorité de Protection des Données à caractère Personnel (APDP).
                </p>
              </Section>

              <Section title="Agences utilisatrices de Recouvro">
                <p>
                  Les agences immobilières qui utilisent Recouvro pour gérer leurs biens et leurs loyers y enregistrent des données sur leurs
                  locataires et propriétaires. Nous les traitons pour le compte de l'agence et selon ses instructions ; pour ces données,
                  adressez-vous d'abord à votre agence.
                </p>
              </Section>

              <Section title="Modifications">
                <p>Cette politique peut évoluer. La date de dernière mise à jour figure en haut de cette page.</p>
              </Section>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default PrivacyPage;
